import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { message, Collapse, Tag } from 'antd';
import { Element4, ArrowUp2 } from 'iconsax-react';

import FormSectionTitle from '../../FormSectionTitle';
import EmptyState from '../../EmptyState';
import CustomModal from '../../../atoms/CustomModal';
import InfoText from '../../../atoms/InfoText';
import SpecialtyForm from './SpecialtyForm';

import { useProfileCreationFormContext } from '../../ProfileCreationFormProvider';
import { incrementCurrentStep, triggerChangePercentage } from '../../../../store/slices/sellerProfileSlice';
import { useGetUserCategoryQuery } from '../../../../store/services/sellerProfileCreationService';

export default function Category({ isReview = false }: { isReview?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { registerSubmit } = useProfileCreationFormContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  const { data: categories, refetch: refetchCategories, isLoading: categoryLoading } = useGetUserCategoryQuery({ profileId });

  const onSubmit = () => {
    if (categories?.payload?.length && !categoryLoading) {
      dispatch(incrementCurrentStep());
      dispatch(triggerChangePercentage());
    } else {
      message.error(t('components.categoryRequired'));
    }
  };

  useEffect(() => {
    if (isReview) return;
    registerSubmit(onSubmit);
  }, [registerSubmit, isReview, onSubmit]);

  return (
    <div data-testid="profile-creation-category-container">
      <FormSectionTitle
        buttonId="ADD_CATEGORY"
        title={t('components.categoryIndustryAndSpecialties')}
        subTitle={t('components.categorysubTitle')}
        buttonText={categories?.payload?.length ? `${t('components.edit')} ${t('components.specialty')}` : t('components.categoryAddSpeciality')}
        onClick={() => {
          setModalData(categories?.payload?.[0] || null);
          setModalOpen(true);
        }}
      />

      {categoryLoading ? (
        <p className="dark:text-dark-text-primary" data-testid="profile-creation-category-loading">{t('components.loading')}</p>
      ) : categories?.payload?.length ? (
        <div className="flex flex-col gap-4">
          {categories.payload.map((s: any) => (
            <div
              key={s.id}
              className="p-4 border border-light-input-border dark:border-dark-input-border rounded-lg bg-white dark:bg-dark-surface-secondary"
            >
              <Collapse
                bordered={false}
                className="bg-transparent [&_.ant-collapse-content-box]:!px-0 [&_.ant-collapse-content-box]:!pt-4 [&_.ant-collapse-header]:!px-0 [&_.ant-collapse-item]:!border-b-0"
                expandIconPosition="end"
                defaultActiveKey={[s.id]}
                expandIcon={({ isActive }) => (
                  <ArrowUp2
                    variant="Bold"
                    className={isActive ? 'transform rotate-180' : ''}
                    size={16}
                  />
                )}
              items={[
                {
                  key: s.id,
                  label: (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-dark-bg-secondary flex items-center justify-center p-2 text-black dark:text-white">
                        <Element4 size="24" variant="Bold" />
                      </div>
                      <div className="flex flex-col">
                        <InfoText text={t('components.categoryLabel')} size="xs" className="text-gray-500 dark:text-gray-400" />
                        <InfoText text={s.name} className="font-semibold text-lg text-black dark:text-white" />
                      </div>
                    </div>
                  ),
                  children: (
                    <div className="flex items-center gap-2 flex-wrap">
                      {s.specialties?.map((ind: { id: string; name: string }) => (
                        <Tag key={ind.id} className="bg-white dark:bg-black-800 text-gray-800 dark:text-gray-200 py-1 px-3 rounded-md text-sm border border-gray-200 dark:border-gray-700 m-0">
                          {ind.name}
                        </Tag>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
            </div>
          ))}
        </div>
      ) : (
        <EmptyState onAdd={() => {
          setModalData(null);
          setModalOpen(true);
        }} />
      )}

      <CustomModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={t('components.categoryIndustryAndSpecialties')}
        description={t('components.categoryAddIndustrialSpecialties')}
        variant="supplier"
        withoutLogo={false}
        centered
      >
        {modalOpen && (
          <SpecialtyForm
            profileId={profileId}
            initialData={modalData}
            onSuccess={() => {
              setModalOpen(false);
              refetchCategories();
            }}
          />
        )}
      </CustomModal>
    </div>
  );
}
